import { enterFullscreen, exitFullscreen } from "./scrollLock.js";

/**
 * Keeps the whole site in real (OS-chrome-hiding) fullscreen while a phone is
 * held in landscape — not just while some overlay happens to be open. A
 * landscape phone loses a large share of its already-short viewport to the
 * browser's address bar / OS nav bar, more than in any other orientation.
 *
 * `(pointer: coarse)` scopes this to touchscreens: a desktop window is
 * "landscape"-shaped essentially always, and auto-fullscreening it just
 * because of that would be unwanted — this is specifically the phone-turned-
 * sideways case.
 */
const query = window.matchMedia("(pointer: coarse) and (orientation: landscape)");

let wantsImmersive = false;

function isFullscreen() {
  return Boolean(document.fullscreenElement || document.webkitFullscreenElement);
}

function tryEnter() {
  if (wantsImmersive && !isFullscreen()) enterFullscreen(document.documentElement);
}

/**
 * The Fullscreen API normally requires an active user gesture, but there's a
 * specific exception in the spec for exactly this case: requestFullscreen()
 * is also allowed when called synchronously inside the handler for a
 * genuine, user-generated orientation-change event (fullscreen.spec.whatwg.org
 * step 5: "...or the algorithm is triggered by a user generated orientation
 * change"). Chromium shipped this in 2016, so it's long since been in every
 * modern Chrome/Edge/Firefox on Android — this is what lets the site go
 * fullscreen the instant you rotate, no tap required. It must be the actual
 * orientation event (screen.orientation's "change", or the legacy
 * window "orientationchange"), not a generic matchMedia change, and the call
 * must happen synchronously in that handler, not deferred.
 *
 * iOS Safari doesn't implement the Fullscreen API for arbitrary elements at
 * all, so this — and the tap fallback below — are harmless no-ops there.
 */
function onOrientationChange() {
  wantsImmersive = query.matches;
  if (!wantsImmersive) {
    if (isFullscreen()) exitFullscreen();
    return;
  }
  tryEnter();
}

export function initImmersiveMode() {
  wantsImmersive = query.matches;

  if (screen.orientation) {
    screen.orientation.addEventListener("change", onOrientationChange);
  } else {
    // Older browsers without the Screen Orientation API: no gesture-free
    // exception applies here, but still track landscape/portrait so exit
    // (which never needs a gesture) and the tap fallback below still work.
    query.addEventListener("change", onOrientationChange);
  }

  // Fallback for any case the orientation-triggered request above doesn't
  // succeed: catch up on the very next real tap, anywhere on the site.
  document.addEventListener("click", tryEnter, { capture: true });
  document.addEventListener("touchend", tryEnter, { capture: true });
}
